import publicRuntimeConfig from "@FRAMEWORK/config/public";
import useConfigureAxios from "@FRAMEWORK/utils/useConfigureAxios";
import {Response} from "@QUERIES/types";
import {UseQueryOptions, useQuery} from "@tanstack/react-query";

interface CompareScheduleList {
	evCd: string;
	singleTypeCd: string;
	trTp: string;
	goodsCd: string;
	goodsTp: string;
	daySeq: number;
	shopCnt: number;
	inclInfo: string;
	notinclInfo: string;
	miscInfo: string;
	goodsInfo: string;
	vimeoUrl: string;
	shopInfo: string;
	poliInfo: string;
	cancelInfo: string;
	optInfo: string;
	refeInfo: string;
	essenInfo: string;
	passportInfo: string;
	foldAttaFileKey: string;
	stretAttaFileKey: string;
	meetingnoyn: "Y";
	meetingnm: string;
	meetingtm: string;
	meetingmanager: string;
	meetingtelno: string;
	meetingnote: string;
}

interface CompareScheduleTimeList {
	goodsCd: string;
	dayNo: number;
	daySeq: number;
	dayNoSeq: number;
	cityNm: string;
	cityCd: string;
	viCityNm: null;
	tmNo: number;
	tmSeq: number;
	tmNm: null;
	tmTitle: string;
	tmContent: string;
	flightTm: null;
	trvVideoUrl: null;
	trvVideoTitle: null;
	trvVideoNote: null;
	trvVimeoUrl: null;
	trvVideoThum: null;
	evStartDt: string;
	evArriveDt: string;
	outDayDiff: number;
	inDayDiff: number;
	outDeprtTm: string;
	outArrvTm: string;
	inDeprtTm: string;
	inArrvTm: string;
	outFlightNm: string;
	inFlightNm: string;
	meetAirPlace: string;
	meetAirTm: null;
	meetAirTelNo: null;
	meetAirNote: null;
	trTp: string;
	outDeprtCityNm: null;
	outArrvCityNm: null;
	inDeprtCityNm: null;
	inArrvCityNm: null;
	endInfoImgUrl: null;
	meetAirImgUrl: null;
	meetAirDivi: string;
	meetAirDiviCont: string;
	startCityNm: string;
}

interface CompareList {
	goodsCd: string;
	evCd: string;
	evNm: string;
	adtPrice: number;
	chdPrice: number;
	infPrice: number;
	evNightNm: string;
	outAirCd: string;
	outAirCompany: string;
	outFlightNm: string;
	outStartDt: string;
	outDeprtTm: string;
	outDeprtFg: string;
	outStartWday: string;
	inArriveDt: string;
	inArrvTm: string;
	inArriveWday: string;
	inFlightNm: string;
	goodsTourInfo: string;
	accomNm: string;
	daySeq: string;
	shopCnt: string;
	evStartDt: string;
	outDayDiff: string;
	outArrvTm: string;
	outArrvCityNm: null;
	webLinkUrl: string;
	webDisplayCls: string;
	dspMenu: string;
	dspSid: string;
	trTp: string;
}

interface CompareScheduleDayList {
	scheTmCnt: number;
	optCnt: number;
	trv70Cnt: number;
	trv20Cnt: number;
	shopInfoCnt: number;
	scheTrvCnt: number;
	shopCnt: number;
	insuSeq: number;
	optInfo: string;
	atteInfo: string;
	insuTempNm: string;
	rewordId: string;
	evStartDt: string;
	goodsCd: string;
	evCd: string;
	daySeq: number;
	dayNoSeq: number;
	dayNo: number;
	accommCd: string;
	accommNm: string;
	accommNote: string;
	accommYn: string;
	foodB: string;
	foodL: string;
	foodD: string;
	foodBYn: string;
	foodLYn: string;
	foodDYn: string;
	accommInfoId: string;
	hotelCnt: number;
}

interface CompareSelTripList {
	goodsCd: string;
	evCd: string;
	title: string;
	intro: string;
	useTm: string;
	cost: string;
	note: string;
	imageUrl: string;
}

interface EventCompareType {
	compareList: Array<CompareList> /* 행사 정보 리스트 */;
	compareScheduleList: Array<CompareScheduleList> /* 행사 일정 마스터 정보 - 포함, 불포함, 참고/전달사항 */;
	compareScheduleTimeList: Array<Array<CompareScheduleTimeList>> /* 행사 시간별 일정 리스트 (주요일정) */;
	compareScheduleDayList: Array<Array<CompareScheduleDayList>> /* 행사 일정 리스트 */;
	compareSelTripList: Array<Array<CompareSelTripList>> /* 선택관광 정보 조회 */;
}

interface EventCompareRequest {
	compareGoodsCd: string;
	compareDate: string;
	compareDspSid: string;
	compareSingleTypeCd: string;
}

/** ```
 * 상품 비교하기
 *   `/prdt/api/goods/compare
 * parameter
 *   `compareGoodsCd` 상품코드 ex) "ATP1127,ATP1164"
 * ```*/
const key = "useEventCompare";
const getPath = (data: EventCompareRequest): string | null => {
	if (!data.compareGoodsCd || !data.compareDate || !data.compareSingleTypeCd || !data.compareDspSid) return null;
	return `${publicRuntimeConfig.ENV_PRODUCT_API_BASEPATH}/api/event/compare?compareGoodsCd=${data.compareGoodsCd}&compareDate=${data.compareDate}&compareDspSid=${data.compareDspSid}&compareSingleTypeCd=${data.compareSingleTypeCd}`;
};

/** ```
 * 상품 비교하기
 *   `/prdt/api/goods/compare
 * parameter
 *   `compareGoodsCd` 상품코드 ex) "ATP1127,ATP1164"
 * ```*/
const eventCompareAsync = async (data: EventCompareRequest) => {
	const result = await useConfigureAxios(getPath(data), {method: "GET"});
	return result;
};

/** ```
 * 상품 비교하기
 *   `/prdt/api/goods/compare
 * parameter
 *   `compareGoodsCd` 상품코드 ex) "ATP1127,ATP1164"
 * ```*/
const useEventCompare = (
	data: EventCompareRequest,
	options?: Partial<UseQueryOptions<Response<EventCompareType>, Error>>,
) =>
	useQuery({
		queryKey: [key, getPath(data)],
		queryFn: () => useConfigureAxios({url: getPath(data)}).then(r => r.data),
		...options,
	});

export default useEventCompare;
export {eventCompareAsync, getPath, key};
export type {
	CompareList,
	CompareScheduleDayList,
	CompareScheduleList,
	CompareScheduleTimeList,
	CompareSelTripList,
	EventCompareRequest,
	EventCompareType,
};
